import Image from "next/image";

import { cn } from "@/src/lib/utils";
import { Heading, Text } from "@/src/components/ui/Typography";

import type { TestimonialItemData } from "./TestimonialCard.types";

interface TestimonialCardProps {
  item: TestimonialItemData;
  index: number;
  open: boolean;
  onSelect: () => void;
  collapsedWidth: string;
  expandedWidth: string;
}

const TRANSITION = "duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]";

export function TestimonialCard({
  item,
  index,
  open,
  onSelect,
  collapsedWidth,
  expandedWidth,
}: TestimonialCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-expanded={open}
      style={{ width: open ? expandedWidth : collapsedWidth }}
      className={cn(
        "relative shrink-0 self-stretch overflow-hidden border-r border-neutral-100 text-left transition-[width] last:border-r-0",
        TRANSITION,
      )}
    >
      {/* The index is the one element that actually moves: from centered in
          the collapsed column to sitting beside the photo. A single number
          traveling, never two numbers cross-fading. */}
      <Text
        as="span"
        size="titleLG"
        style={{
          top: open ? "6rem" : "2rem",
          left: open ? "2rem" : `calc(${collapsedWidth} / 2)`,
          transform: open ? "translateX(0)" : "translateX(-50%)",
        }}
        className={cn(
          "pointer-events-none absolute text-neutral-700 transition-[left,top,transform]",
          TRANSITION,
        )}
      >
        {index}
      </Text>
      
      <div aria-hidden={!open} style={{ width: expandedWidth }} className="pt-14 pr-8 pb-8 pl-32">
        <div className="flex items-center gap-4">
          {item.avatarSrc && (
            <Image
              src={item.avatarSrc}
              alt={item.name}
              width={56}
              height={56}
              className="size-14 shrink-0 rounded-md object-cover"
            />
          )}

          <div>
            <Heading as="h3" size="headingMD" className="text-neutral-700">
              {item.name}
            </Heading>

            <Text size="bodySM" className="text-neutral-500">
              {item.role}
            </Text>
          </div>
        </div>

        <Text
          as="div"
          size="bodyLG"
          className="mt-6 text-neutral-600 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_h2]:mt-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-neutral-700 [&_h3]:mt-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-neutral-700 [&_strong]:text-neutral-700 [&_a]:text-brand [&_a]:underline [&_pre]:mt-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-neutral-100 [&_pre]:p-3 [&_pre]:text-sm"
          dangerouslySetInnerHTML={{ __html: item.quoteHtml }}
        />
      </div>
    </button>
  );
}
